'use client';

import { useQuery } from '@tanstack/react-query';
import { useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { Env } from '@/consts/Env';
import { useIsMobile } from '@/hooks/useIsMobile';
import { authApi } from '@/queries/auth/auth.api';
import { useUserMeQuery } from '@/queries/user/user.queries';
import { useAppSelector } from '@/redux/hooks';
import { runWhenIdle } from '@/utils/runWhenIdle';
import { bootIntercom, installIntercomQueue } from './intercomQueue';

export default function BoostIntercom() {
  const { data: user } = useUserMeQuery();
  const isMobile = useIsMobile();
  const userSession = useAppSelector(state => state.user.userSession);
  const hideIntercom = !!isMobile;
  // The widget is ~300 KB of JS: load it once the page is idle, or right away when the user asks for support.
  const [canLoadWidget, setCanLoadWidget] = useState(false);

  useEffect(() => {
    if (!Env.NEXT_PUBLIC_INTERCOM_APP_ID) {
      return;
    }

    const loadWidget = () => setCanLoadWidget(true);
    installIntercomQueue(loadWidget);
    return runWhenIdle(loadWidget);
  }, []);

  const { data: intercomToken } = useQuery({
    queryKey: ['intercomToken'],
    queryFn: () => authApi.intercomToken(),
    enabled: !!userSession,
  });
  useEffect(() => {
    if (window.Intercom) {
      window.Intercom('update', { hide_default_launcher: hideIntercom });
    }
  }, [isMobile]);
  useEffect(() => {
    if (!Env.NEXT_PUBLIC_INTERCOM_APP_ID || !canLoadWidget) {
      return;
    }

    if (!userSession) {
      bootIntercom({
        app_id: Env.NEXT_PUBLIC_INTERCOM_APP_ID,
      });
      return;
    }

    if (intercomToken && user) { // && preferences
      bootIntercom({
        app_id: Env.NEXT_PUBLIC_INTERCOM_APP_ID,
        user_id: user.id,
        name: `${user.firstName} ${user?.lastName}`,
        email: user.email,
        created_at: user.createdAt ? new Date(user.createdAt).getTime() : undefined,
        intercom_user_jwt: intercomToken.token,
        hide_default_launcher: hideIntercom,
      });
    }
  }, [canLoadWidget, intercomToken, user, userSession]);

  const [openSupport] = useQueryState('openSupport');

  useEffect(() => {
    if (openSupport === 'true') {
      window.Intercom?.('show');
    }
  }, [openSupport]);

  useEffect(() => {
    const shutdownIfNeeded = () => {
      // TODO: Change key to consent and recheck logic
      const consent = window.Cookiebot?.consents;
      if (!consent?.marketing) {
        console.warn('[Intercom] Shutting down due to consent withdrawal');
        window.Intercom?.('shutdown');
      }
    };

    window.addEventListener('CookieConsentDeclaration', shutdownIfNeeded);
    return () => {
      window.removeEventListener('CookieConsentDeclaration', shutdownIfNeeded);
    };
  }, []);

  return null;
}
